import {
  DEADLINE_ERROR_CODES,
  type DeadlineAiPreviewDto,
  type DeadlineAiProposal,
  MAX_DEADLINE_DOCUMENT_SIZE_BYTES,
  MAX_DEADLINE_DOCUMENT_SIZE_MB,
} from '@prospero/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConsultantDeadlineActionsService } from '../../services/consultant/consultant-deadline-actions.service';
import type { IConsultantDeadlineActionsService } from '../../services/consultant/consultant-deadline-actions.service.interface';
import { DeadlineService, type IDeadlineService } from '../../services/deadline/deadline.service';
import { getDeadlineErrorCode } from '../../utils/deadline-error.utils';
import { getHttpErrorMessage, getHttpErrorStatus } from '../../utils/error.utils';

export type DeadlineReadingStatus =
  | 'idle'
  | 'reading'
  /** Caricamento senza lettura IA in corso. */
  | 'uploading'
  | 'read'
  /** Caricato senza lettura IA: si compila a mano. */
  | 'attached'
  | 'failed'
  | 'quota'
  | 'unavailable'
  | 'timeout'
  | 'expired'
  /** Nessuna risposta utile del server (rete, timeout, file rifiutato): il file è stato tolto. */
  | 'notAttached';

export type DeadlineReadingScope = { kind: 'tenant' } | { kind: 'consultant'; tenantId: string };

export interface PickDocumentOptions {
  /** `true` = "Carica e compila con l'IA" (usa la quota); `false` = solo allegato. */
  read: boolean;
}

export interface AdoptDocumentOptions {
  /** L'IA ha letto il file e proposto dei campi: stato `read` (avviso art. 50), altrimenti `attached`. */
  aiRead: boolean;
}

export interface DeadlineDocumentReading {
  file: File | null;
  status: DeadlineReadingStatus;
  /** Monouso: sostituisce il file nel salvataggio del dialog. */
  uploadToken: string | null;
  /** Messaggio del server quando rifiuta il file (400). */
  rejectionMessage: string | null;
  /** La lettura automatica della settimana del cliente è finita: il pulsante IA si disattiva. */
  aiQuotaExhausted: boolean;
  /**
   * Una nuova scelta annulla la lettura del file precedente. Senza `options` il file si legge con
   * l'IA; con `{ read: false }` si allega soltanto.
   */
  pickFile: (file: File | null, options?: PickDocumentOptions) => void;
  /** Il server ha rifiutato il token (404): file e token vanno scelti di nuovo, o il file tolto. */
  markExpired: () => void;
  /** Toglie il file e annulla la lettura in corso. */
  discard: () => void;
  /** Attende la lettura in corso e restituisce il token, se c'è. */
  settle: () => Promise<string | null>;
  /**
   * Token già in deposito (es. dall'assistente): nessuna nuova lettura, nessuna quota. Con
   * `aiRead` la proposta dell'IA è nel dialog: togliere o cambiare il file la ritira.
   */
  adopt: (uploadToken: string, filename: string, options: AdoptDocumentOptions) => void;
}

interface ReadingState {
  file: File | null;
  status: DeadlineReadingStatus;
  uploadToken: string | null;
  rejectionMessage: string | null;
}

const IDLE_STATE: ReadingState = {
  file: null,
  status: 'idle',
  uploadToken: null,
  rejectionMessage: null,
};

const PREVIEW_STATUS_TO_READING: Record<DeadlineAiPreviewDto['status'], DeadlineReadingStatus> = {
  READ: 'read',
  FAILED: 'failed',
  QUOTA_EXHAUSTED: 'quota',
  UNAVAILABLE: 'unavailable',
  NOT_READ: 'attached',
};

const HTTP_BAD_REQUEST = 400;
const HTTP_PAYLOAD_TOO_LARGE = 413;

const DEADLINE_AI_QUOTA_QUERY_KEY = 'deadline-ai-quota';

function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === 'TimeoutError';
}

function rejectionMessageOf(error: unknown): string | null {
  return getHttpErrorStatus(error) === HTTP_BAD_REQUEST
    ? (getHttpErrorMessage(error) ?? null)
    : null;
}

interface FailedRequest {
  file: File;
  error: unknown;
  read: boolean;
  keepFile: boolean;
  tooLargeMessage: string;
}

function tooLargeState(tooLargeMessage: string): ReadingState {
  return {
    file: null,
    status: 'notAttached',
    uploadToken: null,
    rejectionMessage: tooLargeMessage,
  };
}

/** Senza risposta del server non c'è token: o il dialog toglie il file, o lo manda lui al salvataggio. */
function stateAfterFailure({
  file,
  error,
  read,
  keepFile,
  tooLargeMessage,
}: FailedRequest): ReadingState {
  // Il 413 arriva anche da nginx, con un corpo HTML: un file così non passa neanche al salvataggio.
  if (getHttpErrorStatus(error) === HTTP_PAYLOAD_TOO_LARGE) {
    return tooLargeState(tooLargeMessage);
  }
  const rejectionMessage = rejectionMessageOf(error);
  if (!keepFile) {
    return { file: null, status: 'notAttached', uploadToken: null, rejectionMessage };
  }
  if (!read) {
    // Il file resta e viaggia al salvataggio: solo un rifiuto del server merita un messaggio.
    return {
      file,
      status: rejectionMessage ? 'failed' : 'attached',
      uploadToken: null,
      rejectionMessage,
    };
  }
  if (isTimeoutError(error)) {
    return { file, status: 'timeout', uploadToken: null, rejectionMessage: null };
  }
  return { file, status: 'failed', uploadToken: null, rejectionMessage };
}

interface DocumentRequest {
  file: File;
  read: boolean;
  signal: AbortSignal;
}

interface UseDeadlineDocumentReadingOptions {
  scope: DeadlineReadingScope;
  /** Chiamata una volta per lettura riuscita, con la proposta grezza (soglia 0.6 esclusa). */
  onProposal: (proposal: DeadlineAiProposal) => void;
  /**
   * Il rinnovo, se la lettura non restituisce un token, invia il file stesso al salvataggio
   * (`true`); la creazione salva solo tramite token, quindi toglie il file (`notAttached`).
   */
  keepFileWithoutToken?: boolean;
  /** Il dialog è aperto sul suo cliente: solo allora si chiede se la quota IA è finita. */
  quotaEnabled?: boolean;
  /**
   * La proposta consegnata con `onProposal` non vale più: nuovo file (con o senza IA), file tolto o
   * token rifiutato. Il dialog toglie le etichette IA e i valori dell'IA non cambiati a mano.
   */
  onProposalWithdrawn?: () => void;
}

function quotaScopeKey(scope: DeadlineReadingScope): readonly string[] {
  return scope.kind === 'consultant'
    ? [DEADLINE_AI_QUOTA_QUERY_KEY, 'consultant', scope.tenantId]
    : [DEADLINE_AI_QUOTA_QUERY_KEY, 'tenant'];
}

function hasQuotaTarget(scope: DeadlineReadingScope): boolean {
  return scope.kind === 'tenant' || scope.tenantId !== '';
}

/**
 * La lettura avviene nel dialog al momento della scelta del file: il file passa una volta sola dal
 * server, che lo tiene in deposito temporaneo e restituisce un token da inviare al salvataggio.
 * Senza lettura il file fa la stessa strada (deposito e token), senza modello e senza quota.
 */
export function useDeadlineDocumentReading({
  scope,
  onProposal,
  keepFileWithoutToken = false,
  quotaEnabled = false,
  onProposalWithdrawn,
}: UseDeadlineDocumentReadingOptions): DeadlineDocumentReading {
  const tenantService = useInjection<IDeadlineService>(DeadlineService);
  const consultantService = useInjection<IConsultantDeadlineActionsService>(
    ConsultantDeadlineActionsService,
  );
  const queryClient = useQueryClient();
  const { t } = useTranslation('documents');
  const tooLargeMessage = t('deadlines.renew.fileTooLarge', {
    maxMb: MAX_DEADLINE_DOCUMENT_SIZE_MB,
  });
  const [state, setState] = useState<ReadingState>(IDLE_STATE);
  const latestRunRef = useRef(0);
  const pendingRef = useRef<Promise<string | null> | null>(null);
  const tokenRef = useRef<string | null>(null);
  /** Annulla la richiesta in corso: il server smette di leggere e restituisce la quota. */
  const abortRef = useRef<AbortController | null>(null);
  const onProposalRef = useRef(onProposal);
  onProposalRef.current = onProposal;
  const onProposalWithdrawnRef = useRef(onProposalWithdrawn);
  onProposalWithdrawnRef.current = onProposalWithdrawn;
  /** Una proposta è nel dialog: il prossimo cambio di file la ritira. */
  const hasProposalRef = useRef(false);

  const quotaKey = quotaScopeKey(scope);
  const { data: aiQuota } = useQuery({
    queryKey: quotaKey,
    queryFn: () =>
      scope.kind === 'consultant'
        ? consultantService.getAiQuota(scope.tenantId)
        : tenantService.getAiQuota(),
    enabled: quotaEnabled && hasQuotaTarget(scope),
  });

  const { mutateAsync: sendDocument } = useMutation({
    mutationFn: ({ file, read, signal }: DocumentRequest) => {
      if (scope.kind === 'consultant') {
        return read
          ? consultantService.previewDocument(scope.tenantId, file, signal)
          : consultantService.stageDocument(scope.tenantId, file, signal);
      }
      return read
        ? tenantService.previewDocument(file, signal)
        : tenantService.stageDocument(file, signal);
    },
  });

  /** Ogni nuova scelta, rimozione o rifiuto del token rende inutile la risposta della lettura in corso. */
  const startNewRun = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    latestRunRef.current += 1;
    pendingRef.current = null;
    tokenRef.current = null;
    if (hasProposalRef.current) {
      hasProposalRef.current = false;
      onProposalWithdrawnRef.current?.();
    }
    return latestRunRef.current;
  }, []);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const discard = useCallback(() => {
    startNewRun();
    setState(IDLE_STATE);
  }, [startNewRun]);

  const quotaKeyRef = useRef(quotaKey);
  quotaKeyRef.current = quotaKey;

  const send = useCallback(
    async (request: DocumentRequest, runId: number): Promise<string | null> => {
      const { file, read } = request;
      try {
        const result = await sendDocument(request);
        if (read) {
          // La lettura ha consumato (o trovato finita) la quota: il pulsante IA si aggiorna.
          void queryClient.invalidateQueries({ queryKey: quotaKeyRef.current });
        }
        if (runId !== latestRunRef.current) {
          return null;
        }
        tokenRef.current = result.uploadToken;
        setState({
          file,
          status: PREVIEW_STATUS_TO_READING[result.status],
          uploadToken: result.uploadToken,
          rejectionMessage: null,
        });
        if (result.status === 'READ' && result.proposal) {
          hasProposalRef.current = true;
          onProposalRef.current(result.proposal);
        }
        return result.uploadToken;
      } catch (error) {
        if (runId === latestRunRef.current) {
          setState(
            stateAfterFailure({
              file,
              error,
              read,
              keepFile: keepFileWithoutToken,
              tooLargeMessage,
            }),
          );
        }
        return null;
      }
    },
    [sendDocument, keepFileWithoutToken, queryClient, tooLargeMessage],
  );

  const pickFile = useCallback(
    (file: File | null, options: PickDocumentOptions = { read: true }) => {
      if (file === null) {
        discard();
        return;
      }
      const runId = startNewRun();
      if (file.size > MAX_DEADLINE_DOCUMENT_SIZE_BYTES) {
        setState(tooLargeState(tooLargeMessage));
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setState({
        file,
        status: options.read ? 'reading' : 'uploading',
        uploadToken: null,
        rejectionMessage: null,
      });
      pendingRef.current = send({ file, read: options.read, signal: controller.signal }, runId);
    },
    [discard, send, startNewRun, tooLargeMessage],
  );

  const markExpired = useCallback(() => {
    startNewRun();
    setState({ file: null, status: 'expired', uploadToken: null, rejectionMessage: null });
  }, [startNewRun]);

  const settle = useCallback(async (): Promise<string | null> => {
    const pending = pendingRef.current;
    return pending ? await pending : tokenRef.current;
  }, []);

  const adopt = useCallback(
    (uploadToken: string, filename: string, { aiRead }: AdoptDocumentOptions) => {
      startNewRun();
      tokenRef.current = uploadToken;
      hasProposalRef.current = aiRead;
      setState({
        file: new File([], filename),
        status: aiRead ? 'read' : 'attached',
        uploadToken,
        rejectionMessage: null,
      });
    },
    [startNewRun],
  );

  return {
    ...state,
    aiQuotaExhausted: aiQuota?.exhausted === true,
    pickFile,
    markExpired,
    discard,
    settle,
    adopt,
  };
}

/** Il 404 del token si distingue dagli altri 404 dal codice stabile del server. */
export function isUploadTokenInvalidError(error: unknown): boolean {
  return getDeadlineErrorCode(error) === DEADLINE_ERROR_CODES.UPLOAD_TOKEN_INVALID;
}
